import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const OVERFLOW_TOGGLE_WIDTH_PX = 48;
const ITEM_GAP_PX = 6;
const REORDER_DRAG_THRESHOLD_PX = 6;
const REORDER_BLOCK_SELECTOR =
  "input, textarea, select, option, [contenteditable='true']";

interface ToolbarOverflowRowProps {
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  itemOrder?: readonly string[];
  onReorder?: (sourceKey: string, targetKey: string) => void;
}

interface DropdownMenuPortalProps extends React.HTMLAttributes<HTMLDivElement> {
  show?: boolean;
  close?: () => void;
  popper?: unknown;
}

const DropdownMenuPortal = React.forwardRef<
  HTMLDivElement,
  DropdownMenuPortalProps
>(function DropdownMenuPortal(
  { children, show: _show, close: _close, popper: _popper, ...domProps },
  ref,
) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      ref={ref}
      {...domProps}
      data-eeg-workspace-theme={
        typeof document !== "undefined"
          ? document.body.getAttribute("data-eeg-workspace-theme") ||
            document.documentElement.getAttribute("data-eeg-workspace-theme") ||
            undefined
          : undefined
      }
    >
      {children}
    </div>,
    document.body,
  );
});

function normalizeChildren(children: React.ReactNode): React.ReactElement[] {
  return React.Children.toArray(children).filter(
    (child): child is React.ReactElement =>
      React.isValidElement(child) && child.type !== React.Fragment,
  );
}

export function toolbarChildReorderKey(
  child: React.ReactElement,
  index: number,
): string {
  if (child.key == null || child.key === "") {
    return String(index);
  }
  return String(child.key).replace(/^\.\$/, "");
}

export function sortToolbarChildrenByOrder(
  items: React.ReactElement[],
  itemOrder: readonly string[],
): React.ReactElement[] {
  if (itemOrder.length === 0) {
    return items;
  }
  const rank = new Map(itemOrder.map((key, index) => [key, index]));
  return [...items].sort((left, right) => {
    const leftRank = rank.get(toolbarChildReorderKey(left, 0));
    const rightRank = rank.get(toolbarChildReorderKey(right, 1));
    if (leftRank == null && rightRank == null) return 0;
    if (leftRank == null) return 1;
    if (rightRank == null) return -1;
    return leftRank - rightRank;
  });
}

export function ToolbarOverflowRow({
  children,
  className = "",
  ariaLabel,
  itemOrder,
  onReorder,
}: ToolbarOverflowRowProps) {
  const { t } = useTranslation("features");
  const childItems = sortToolbarChildrenByOrder(
    normalizeChildren(children),
    itemOrder ?? [],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef<HTMLDivElement>(null);
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;
  const dragRef = useRef<{
    key: string;
    pointerId: number;
    startX: number;
    startY: number;
    active: boolean;
  } | null>(null);
  const htmlDragKeyRef = useRef<string | null>(null);
  const dropKeyRef = useRef<string | null>(null);
  const suppressClickRef = useRef(false);
  const trackReorderDragRef = useRef<(event: PointerEvent) => void>(() => {});
  const finishReorderDragRef = useRef<(event: PointerEvent) => void>(() => {});
  const onWindowPointerMove = useRef((event: PointerEvent) => {
    trackReorderDragRef.current(event);
  }).current;
  const onWindowPointerUp = useRef((event: PointerEvent) => {
    finishReorderDragRef.current(event);
  }).current;
  const [visibleCount, setVisibleCount] = useState(childItems.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);

  trackReorderDragRef.current = (event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - drag.startX,
      event.clientY - drag.startY,
    );
    if (!drag.active) {
      if (distance < REORDER_DRAG_THRESHOLD_PX) {
        return;
      }
      drag.active = true;
      setDragKey(drag.key);
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    const node = document.elementFromPoint(event.clientX, event.clientY);
    const item = node?.closest(
      "[data-toolbar-reorder-key]",
    ) as HTMLElement | null;
    const nextDrop =
      item?.dataset.toolbarReorderKey &&
      item.dataset.toolbarReorderKey !== drag.key
        ? item.dataset.toolbarReorderKey
        : null;
    dropKeyRef.current = nextDrop;
    setDropKey((current) => (current === nextDrop ? current : nextDrop));
  };

  finishReorderDragRef.current = (event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const sourceKey = drag.key;
    const wasActive = drag.active;
    const targetKey = dropKeyRef.current;
    dragRef.current = null;
    dropKeyRef.current = null;
    setDragKey(null);
    setDropKey(null);
    window.removeEventListener("pointermove", onWindowPointerMove);
    window.removeEventListener("pointerup", onWindowPointerUp);
    window.removeEventListener("pointercancel", onWindowPointerUp);
    if (wasActive && targetKey && targetKey !== sourceKey) {
      suppressClickRef.current = true;
      onReorderRef.current?.(sourceKey, targetKey);
    }
  };

  const handleReorderPointerDown = useCallback(
    (itemKey: string, event: React.PointerEvent<HTMLDivElement>) => {
      if (!onReorderRef.current || event.button !== 0) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest(REORDER_BLOCK_SELECTOR)) {
        event.currentTarget.draggable = false;
        return;
      }
      event.currentTarget.draggable = true;
      dragRef.current = {
        key: itemKey,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        active: false,
      };
      window.addEventListener("pointermove", onWindowPointerMove);
      window.addEventListener("pointerup", onWindowPointerUp);
      window.addEventListener("pointercancel", onWindowPointerUp);
    },
    [onWindowPointerMove, onWindowPointerUp],
  );

  useEffect(() => {
    const suppressClickAfterDrag = (event: MouseEvent) => {
      if (!suppressClickRef.current) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    };
    document.addEventListener("click", suppressClickAfterDrag, true);
    return () => {
      document.removeEventListener("click", suppressClickAfterDrag, true);
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
    };
  }, []);

  const recomputeVisibleCount = useCallback((mode: "fit" | "grow" = "fit") => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || childItems.length === 0) {
      setVisibleCount((current) =>
        current === childItems.length ? current : childItems.length,
      );
      return;
    }

    const availableWidth = Math.max(container.clientWidth, 0);

    if (availableWidth <= 0) {
      return;
    }

    const itemNodes = Array.from(
      measure.querySelectorAll<HTMLElement>("[data-toolbar-overflow-item]"),
    );

    if (itemNodes.length === 0) {
      return;
    }

    if (itemNodes.length === 1) {
      setVisibleCount((current) => (current === 1 ? current : 1));
      return;
    }

    let usedWidth = 0;
    let nextVisibleCount = 0;
    const overflowReserve = OVERFLOW_TOGGLE_WIDTH_PX + ITEM_GAP_PX;

    for (const node of itemNodes) {
      const itemWidth = node.offsetWidth;
      const gap = nextVisibleCount > 0 ? ITEM_GAP_PX : 0;
      const nextUsed = usedWidth + gap + itemWidth;
      const remainingAfterThis = itemNodes.length - (nextVisibleCount + 1);
      // Only reserve +N toggle space when more items would still overflow.
      const reserve = remainingAfterThis > 0 ? overflowReserve : 0;

      if (nextVisibleCount > 0 && nextUsed + reserve > availableWidth) {
        break;
      }

      usedWidth = nextUsed;
      nextVisibleCount += 1;
    }

    if (nextVisibleCount >= itemNodes.length) {
      nextVisibleCount = itemNodes.length;
    } else {
      nextVisibleCount = Math.max(1, nextVisibleCount);
    }

    setVisibleCount((current) => {
      const next =
        mode === "fit" ? Math.min(current, nextVisibleCount) : nextVisibleCount;
      return next === current ? current : next;
    });
  }, [childItems.length]);

  useLayoutEffect(() => {
    recomputeVisibleCount("grow");
  }, [recomputeVisibleCount, childItems.length]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      recomputeVisibleCount("grow");
    });
    observer.observe(container);
    const toolbarRow = container.closest(".eeg-toolbar-row--primary");
    if (toolbarRow) observer.observe(toolbarRow);
    return () => observer.disconnect();
  }, [recomputeVisibleCount]);

  useLayoutEffect(() => {
    const visible = visibleRef.current;
    if (!visible) return;
    if (visible.scrollWidth > visible.clientWidth + 1 && visibleCount > 1) {
      setVisibleCount((current) => Math.max(1, current - 1));
    }
  }, [visibleCount, childItems.length]);

  const overflowItems = childItems.slice(visibleCount);
  const visibleItems = childItems.slice(0, visibleCount);
  const showOverflow = overflowItems.length > 0;

  return (
    <div
      ref={containerRef}
      className={`eeg-toolbar-overflow-row${
        onReorder ? " eeg-toolbar-overflow-row--reorderable" : ""
      } ${className}`.trim()}
      aria-label={ariaLabel}
    >
      <div
        ref={measureRef}
        className="eeg-toolbar-overflow-row__measure"
        aria-hidden="true"
      >
        {childItems.map((child, index) => (
          <div
            key={`measure-${child.key ?? index}`}
            data-toolbar-overflow-item
            className="eeg-toolbar-overflow-row__item"
          >
            {React.cloneElement(child)}
          </div>
        ))}
      </div>

      <div
        ref={visibleRef}
        className="eeg-toolbar-overflow-row__visible"
      >
        {visibleItems.map((child, index) => {
          const itemKey = toolbarChildReorderKey(child, index);
          const itemClassName = [
            "eeg-toolbar-overflow-row__item",
            dragKey === itemKey ? "is-dragging" : "",
            dropKey === itemKey ? "is-drop-target" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div
              key={`visible-${child.key ?? index}`}
              className={itemClassName}
              data-toolbar-reorder-key={onReorder ? itemKey : undefined}
              draggable={Boolean(onReorder)}
              onPointerDown={
                onReorder
                  ? (event) => handleReorderPointerDown(itemKey, event)
                  : undefined
              }
              onDragStart={
                onReorder
                  ? (event) => {
                      htmlDragKeyRef.current = itemKey;
                      dragRef.current = null;
                      try {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", itemKey);
                      } catch {
                        // jsdom may not implement dataTransfer
                      }
                      setDragKey(itemKey);
                    }
                  : undefined
              }
              onDragOver={
                onReorder
                  ? (event) => {
                      event.preventDefault();
                      if (event.dataTransfer) {
                        event.dataTransfer.dropEffect = "move";
                      }
                      setDropKey((current) =>
                        current === itemKey ? current : itemKey,
                      );
                    }
                  : undefined
              }
              onDrop={
                onReorder
                  ? (event) => {
                      event.preventDefault();
                      const sourceKey =
                        (event.dataTransfer
                          ? event.dataTransfer.getData("text/plain")
                          : "") || htmlDragKeyRef.current;
                      htmlDragKeyRef.current = null;
                      setDragKey(null);
                      setDropKey(null);
                      if (sourceKey && sourceKey !== itemKey) {
                        onReorder(sourceKey, itemKey);
                      }
                    }
                  : undefined
              }
              onDragEnd={
                onReorder
                  ? () => {
                      htmlDragKeyRef.current = null;
                      setDragKey(null);
                      setDropKey(null);
                    }
                  : undefined
              }
            >
              {child}
            </div>
          );
        })}

        {showOverflow && (
          <Dropdown
            align="end"
            show={menuOpen}
            onToggle={(nextOpen) => setMenuOpen(nextOpen)}
            className="eeg-toolbar-overflow-row__dropdown"
          >
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              className="eeg-toolbar-overflow-row__toggle"
              aria-expanded={menuOpen}
              aria-label={t(
                "ui.eegViewer.toolbar.moreControls",
                "More workspace controls",
              )}
              title={t(
                "ui.eegViewer.toolbar.moreControlsCount",
                "{{count}} more controls",
                { count: overflowItems.length },
              )}
            >
              +{overflowItems.length}
            </Dropdown.Toggle>
            <Dropdown.Menu
              as={DropdownMenuPortal}
              renderOnMount
              className="eeg-toolbar-overflow-row__menu"
              popperConfig={{ strategy: "fixed" }}
            >
              {overflowItems.map((child, index) => (
                <div
                  key={`overflow-${child.key ?? index}`}
                  className="eeg-toolbar-overflow-row__menu-item"
                >
                  {child}
                </div>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

export default ToolbarOverflowRow;
